import { useEffect, useState } from "react";
import { Newspaper, Sparkles, Facebook, Globe, Pencil, RefreshCw, Trash2, Loader2, Power, Send, X, Check } from "lucide-react";
import { api, fmtError } from "../lib/api";
import { toast } from "sonner";

const THEME_OPTIONS = [
  { v: "", label: "Thème automatique (rotation)" },
  { v: "track_week", label: "Morceau de la semaine" },
  { v: "artist_portrait", label: "Portrait d'artiste" },
  { v: "advice", label: "Conseil pro" },
  { v: "new_tracks", label: "Nouveautés du catalogue" },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export const AdminContent = () => {
  const [items, setItems] = useState([]);
  const [auto, setAuto] = useState({ enabled: true, next_due: null });
  const [theme, setTheme] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [subjects, setSubjects] = useState([]); // artistes ou morceaux selon le thème
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // content_id
  const [draft, setDraft] = useState({});

  const load = () =>
    api.get("/admin/content").then((r) => { setItems(r.data.items); setAuto(r.data.auto); }).catch(() => {});

  useEffect(() => { load(); }, []);

  // Charge la liste des sujets possibles quand le thème le permet
  useEffect(() => {
    setSubjectId("");
    if (theme === "artist_portrait") {
      api.get("/admin/artists").then((r) => {
        const arr = Array.isArray(r.data) ? r.data : r.data.artists || [];
        setSubjects(arr.filter((a) => a.artist_status === "approved")
          .map((a) => ({ id: a.user_id, label: a.artist_name })));
      }).catch(() => setSubjects([]));
    } else if (theme === "track_week" || theme === "new_tracks") {
      api.get("/studio/tracks").then((r) => {
        const arr = Array.isArray(r.data) ? r.data : r.data.tracks || [];
        setSubjects(arr.map((t) => ({ id: t.track_id, label: `${t.title} — ${t.artist}` })));
      }).catch(() => setSubjects([]));
    } else {
      setSubjects([]);
    }
  }, [theme]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/admin/content/generate", { theme: theme || null, subject_id: subjectId || null });
      toast.success("Publication générée — relisez-la ci-dessous");
      load();
    } catch (e) { toast.error(fmtError(e)); } finally { setBusy(false); }
  };

  const toggleAuto = async () => {
    try {
      const r = await api.put("/admin/content-auto", { enabled: !auto.enabled });
      setAuto((a) => ({ ...a, enabled: r.data.enabled }));
      toast.success(r.data.enabled ? "Génération automatique activée (lundi et jeudi)" : "Génération automatique en pause");
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const publishArticle = async (id) => {
    try {
      await api.post(`/admin/content/${id}/publish-article`);
      toast.success("Article publié sur la page Journal du site");
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const publishFacebook = async (id) => {
    try {
      await api.post(`/admin/content/${id}/publish-facebook`);
      toast.success("Publié sur votre page Facebook");
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const regenerate = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/content/${id}/regenerate`);
      toast.success("Textes régénérés");
      load();
    } catch (e) { toast.error(fmtError(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce contenu ?")) return;
    try {
      await api.delete(`/admin/content/${id}`);
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const startEdit = (it) => {
    setEditing(it.content_id);
    setDraft({ title: it.title, excerpt: it.excerpt, article_html: it.article_html, caption_fb: it.caption_fb, caption_ig: it.caption_ig, hashtags: it.hashtags });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/content/${editing}`, draft);
      toast.success("Modifications enregistrées");
      setEditing(null);
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const btnCls = "flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-amber-500 hover:text-amber-400 disabled:opacity-40";
  const inputCls = "w-full border border-white/15 bg-transparent px-3 py-2 text-sm outline-none focus:border-amber-500";

  return (
    <section className="border border-white/[0.08] bg-surface p-8" data-testid="admin-content-section">
      <h2 className="flex items-center gap-3 font-display text-2xl font-medium">
        <Newspaper className="h-5 w-5 text-amber-500" /> Contenu automatique
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">
        Chaque <span className="text-zinc-200">lundi et jeudi</span>, une publication complète est générée par l'IA
        (article pour la page Journal + posts Facebook/Instagram). Vous êtes notifié, vous relisez, vous publiez d'un clic.
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button onClick={toggleAuto} data-testid="content-auto-toggle" className={btnCls}>
          <Power className={`h-4 w-4 ${auto.enabled ? "text-emerald-400" : "text-zinc-500"}`} />
          {auto.enabled ? "Automatique : activé" : "Automatique : en pause"}
        </button>
        {auto.enabled && auto.next_due && (
          <span className="text-xs text-zinc-500" data-testid="content-next-due">
            Prochaine génération : {fmtDate(auto.next_due)}
          </span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <select value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="content-theme-select"
                className="border border-white/15 bg-black px-4 py-2.5 text-sm outline-none focus:border-amber-500">
          {THEME_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        {subjects.length > 0 && (
          <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} data-testid="content-subject-select"
                  className="max-w-full border border-amber-500/40 bg-black px-4 py-2.5 text-sm outline-none focus:border-amber-500">
            <option value="">{theme === "artist_portrait" ? "Artiste au hasard" : "Morceau au hasard"}</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        )}
        <button onClick={generate} disabled={busy} data-testid="content-generate-btn"
                className="flex items-center gap-2 bg-amber-500 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-400 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {busy ? "Génération en cours…" : "Générer maintenant"}
        </button>
      </div>

      <div className="mt-8 space-y-6" data-testid="content-items">
        {items.length === 0 && (
          <p className="text-sm text-zinc-500" data-testid="content-empty">
            Aucun contenu pour l'instant — cliquez « Générer maintenant » ou attendez le prochain créneau automatique.
          </p>
        )}
        {items.map((it) => (
          <article key={it.content_id} data-testid={`content-item-${it.content_id}`}
                   className="border border-white/[0.08] p-5">
            <div className="flex flex-wrap items-center gap-3">
              <span className="bg-amber-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-amber-400">{it.theme_label}</span>
              <span className="text-[10px] text-zinc-600">{fmtDate(it.created_at)}</span>
              {it.article_published_at && <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-400"><Check className="h-3 w-3" /> Article en ligne</span>}
              {it.fb_published_at && <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-400"><Check className="h-3 w-3" /> Sur Facebook</span>}
            </div>

            <div className="mt-4 grid gap-5 md:grid-cols-[140px_1fr]">
              <img src={it.image_url} alt="" data-testid={`content-image-${it.content_id}`}
                   className="h-24 w-full border border-white/10 object-cover md:w-[140px]" loading="lazy" />
              <div className="min-w-0">
                {editing === it.content_id ? (
                  <div className="space-y-3" data-testid={`content-edit-${it.content_id}`}>
                    <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={inputCls} placeholder="Titre" />
                    <input value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} className={inputCls} placeholder="Résumé" />
                    <textarea value={draft.article_html} onChange={(e) => setDraft({ ...draft, article_html: e.target.value })} rows={5} className={inputCls} placeholder="Article (HTML simple : <p>…</p>)" />
                    <textarea value={draft.caption_fb} onChange={(e) => setDraft({ ...draft, caption_fb: e.target.value })} rows={2} className={inputCls} placeholder="Post Facebook" />
                    <textarea value={draft.caption_ig} onChange={(e) => setDraft({ ...draft, caption_ig: e.target.value })} rows={2} className={inputCls} placeholder="Post Instagram" />
                    <input value={draft.hashtags} onChange={(e) => setDraft({ ...draft, hashtags: e.target.value })} className={inputCls} placeholder="Hashtags" />
                    <div className="flex gap-2">
                      <button onClick={saveEdit} data-testid={`content-save-${it.content_id}`}
                              className="flex items-center gap-2 bg-amber-500 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black hover:bg-amber-400">
                        <Check className="h-3.5 w-3.5" /> Enregistrer
                      </button>
                      <button onClick={() => setEditing(null)} className={btnCls}><X className="h-3.5 w-3.5" /> Annuler</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="font-display text-lg font-medium" data-testid={`content-title-${it.content_id}`}>{it.title}</h3>
                    {it.excerpt && <p className="mt-1 text-sm text-zinc-400">{it.excerpt}</p>}
                    <p className="mt-2 whitespace-pre-line text-sm text-zinc-500" data-testid={`content-caption-${it.content_id}`}>{it.caption_fb}</p>
                    {it.hashtags && <p className="mt-1 text-xs text-zinc-600">{it.hashtags}</p>}
                    <a href={it.link} target="_blank" rel="noreferrer" className="mt-1 block truncate text-xs text-amber-500/70 hover:text-amber-400">{it.link}</a>
                  </>
                )}
              </div>
            </div>

            {editing !== it.content_id && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => publishArticle(it.content_id)} disabled={!!it.article_published_at}
                        data-testid={`content-publish-article-${it.content_id}`}
                        className="flex items-center gap-2 bg-white px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-400 disabled:opacity-40">
                  <Globe className="h-3.5 w-3.5" /> {it.article_published_at ? "Article publié" : "Publier l'article"}
                </button>
                <button onClick={() => publishFacebook(it.content_id)} disabled={!!it.fb_published_at}
                        data-testid={`content-publish-fb-${it.content_id}`}
                        className="flex items-center gap-2 bg-[#1877F2] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white transition-colors hover:brightness-110 disabled:opacity-40">
                  <Facebook className="h-3.5 w-3.5" /> {it.fb_published_at ? "Publié" : "Publier sur Facebook"}
                </button>
                <button onClick={() => startEdit(it)} data-testid={`content-edit-open-${it.content_id}`} className={btnCls}>
                  <Pencil className="h-3.5 w-3.5" /> Modifier
                </button>
                <button onClick={() => regenerate(it.content_id)} disabled={busy} data-testid={`content-regenerate-${it.content_id}`} className={btnCls}>
                  <RefreshCw className="h-3.5 w-3.5" /> Régénérer
                </button>
                <button onClick={() => remove(it.content_id)} data-testid={`content-delete-${it.content_id}`}
                        className="flex items-center gap-2 border border-red-500/40 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-red-400 transition-colors hover:bg-red-500 hover:text-black">
                  <Trash2 className="h-3.5 w-3.5" /> Supprimer
                </button>
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
};
